/**
 * Buttons.
 *
 * The prop names deliberately mirror Carbon's (`kind`, `size`, `renderIcon`,
 * `hasIconOnly`, `iconDescription`). Seventeen files import Carbon's Button
 * today; matching the shape means retiring Carbon is an import-path change at
 * each call site rather than seventeen rewrites, and no behaviour moves while
 * the visual layer does.
 *
 * `hasIconOnly` demands `iconDescription` — an icon-only control with no
 * accessible name is a button that only sighted pointer users can operate, and
 * this console has destructive ones.
 */

import type { ButtonHTMLAttributes, ComponentType, ReactNode } from 'react';

import type { IconProps } from './Icon';

export type ButtonKind = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

type NativeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

export interface ButtonProps extends NativeButtonProps {
  kind?: ButtonKind;
  size?: ButtonSize;
  renderIcon?: ComponentType<IconProps>;
  hasIconOnly?: boolean;
  /** Required when `hasIconOnly`: becomes the button's accessible name. */
  iconDescription?: string;
  children?: ReactNode;
}

export function Button({
  kind = 'primary',
  size = 'md',
  renderIcon: Icon,
  hasIconOnly = false,
  iconDescription,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    `btn--${kind}`,
    `btn--${size}`,
    hasIconOnly ? 'btn--icon' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      aria-label={hasIconOnly ? iconDescription : rest['aria-label']}
      title={hasIconOnly ? iconDescription : rest.title}
      {...rest}
    >
      {Icon ? <Icon size={size === 'sm' ? 14 : 16} className="btn__icon" /> : null}
      {hasIconOnly ? null : children}
    </button>
  );
}

/** A link that looks like a button. Same skin, correct semantics for navigation. */
export function ButtonLink({
  kind = 'secondary',
  size = 'md',
  renderIcon: Icon,
  children,
  className,
  ...rest
}: {
  kind?: ButtonKind;
  size?: ButtonSize;
  renderIcon?: ComponentType<IconProps>;
  children: ReactNode;
  className?: string;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const classes = ['btn', `btn--${kind}`, `btn--${size}`, className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <a className={classes} {...rest}>
      {Icon ? <Icon size={size === 'sm' ? 14 : 16} className="btn__icon" /> : null}
      {children}
    </a>
  );
}
